/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Divider,
  List,
  Typography,
  Tooltip,
  Skeleton,
  Select,
  Form,
  notification,
  Modal,
  Button,
} from 'antd';
import {
  EyeOutlined,
  SearchOutlined,
  CloseOutlined,
  CheckOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import ShipmentLogs from './ShipmentLogs';
import {
  OnDeleteShipmentNumber,
  OnFetchShipmentSno,
  OnAddShipmentNumber,
  OnPreviewShipmentNumber,
} from '../../redux/ShipmentDetails/saga';

const { Text } = Typography;
const { Option } = Select;

const ShipmentNo = (props) => {
  const { selectedData } = props;

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchSno, setSearchSno] = useState(false);
  const [equipSnoDrop, setEquipSnoDrop] = useState([]);
  const [equipSnoList, setEquipSnoList] = useState([]);
  const [equipSnoSearchForm] = Form.useForm();
  const [isTableModelVisible, setIsTableModelVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedDataSno, setSelectedDataSno] = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [previewShipmentNumber, setPreviewShipmentNumber] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    if (selectedData) OnfetchData();
  }, [selectedData]);

  useEffect(() => {
    if (selectedData) {
      const fetchShippingForms = async () => {
        try {
          setFetchLoading(true);
          await OnfetchData();
          setFetchLoading(false);
        } catch (error) {
          console.error('Failed to fetch shipping forms:', error);
          setFetchLoading(false);
        }
      };

      fetchShippingForms();
    }
  }, [selectedData]);

  const handleEyeClick = async (record) => {
    setSelectedDataSno(record);
    setIsTableModelVisible(true);
  };

  const OnfetchData = async () => {
    const obj = {
      id: selectedData.id,
    };
    const res = await OnFetchShipmentSno(obj);
    if (res['shipmentType']?.shipments && res['shipmentType'].shipments.length > 0) {
      setEquipSnoList(res['shipmentType'].shipments);
      setEquipSnoDrop(res['shipmentType'].shipments);
    }
  };

  const showDeleteModal = (record) => {
    setSelectedDataSno(record);
    setIsDeleteModalVisible(true);
  };

  const handleOk = async () => {
    setDeleteLoading(true);
    await OnDeleteShipmentNumber(selectedDataSno);
    await OnfetchData();
    setDeleteLoading(false);
    setIsDeleteModalVisible(false);
  };

  const handleCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedDataSno('');
  };

  // Step 1: Initial click to preview shipment number
  const handleAddButtonClick = async () => {
    if (!selectedData || !selectedData.id) {
      notification.error({
        message: 'Cannot add shipment number',
        description: 'No shipment type selected',
      });
      return;
    }

    setAddLoading(true);

    try {
      // Call the preview endpoint to get the next number
      const previewData = await OnPreviewShipmentNumber(selectedData.id);

      if (previewData && previewData.previewShipmentNumber) {
        setPreviewShipmentNumber(previewData.previewShipmentNumber);
        setIsConfirmModalVisible(true);
      } else {
        notification.error({
          message: 'Failed to generate shipment number preview',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error generating shipment number',
        description: error?.message || 'Unknown error',
      });
    } finally {
      setAddLoading(false);
    }
  };

  // Step 2: Handle confirmation (Yes/No)
  const handleConfirmAdd = async () => {
    setAddLoading(true);

    const payload = {
      shipment: {
        shipmentTypeId: selectedData.id,
      },
    };

    const response = await OnAddShipmentNumber(payload);

    if (response?.shipment?.shipmentNumber) {
      notification.success({
        message: `Shipment number ${response.shipment.shipmentNumber} added successfully`,
      });
    }

    // Close the modal
    setIsConfirmModalVisible(false);

    await OnfetchData();
    setAddLoading(false);
  };

  const handleCancelAdd = () => {
    setIsConfirmModalVisible(false);
    setPreviewShipmentNumber('');
  };

  const onSelectEquipSno = () => {
    setSearchSno(true);
  };

  const onSearchEquipmentSno = () => {
    equipSnoSearchForm.validateFields().then(async (data) => {
      if (data && data.shipmentNumber) {
        setSearchSno(false);
        const newArray = equipSnoDrop.find((u) => u.shipmentNumber === data.shipmentNumber);
        setEquipSnoList([newArray]);
      } else {
        notification.warning({
          message: 'Select Equipment Sno to Search.',
        });
      }
    });
  };

  return (
    <div className="equipmentsSno">
      <div className="search_equipSno">
        {!searchOpen ? (
          <Tooltip title={'Search Shipment Number'}>
            <SearchOutlined className="searchIcon" onClick={() => setSearchOpen(true)} />
          </Tooltip>
        ) : (
          <div className="searchDiv">
            <Form form={equipSnoSearchForm}>
              <Form.Item name="shipmentNumber" required>
                <Select
                  className="client-select search_equip"
                  placeholder="Search shipment number"
                  showSearch
                  optionFilterProp="children"
                  onChange={onSelectEquipSno}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {equipSnoDrop &&
                    equipSnoDrop.map((item, i) => (
                      <Option key={i} value={item && item.shipmentNumber}>
                        {item && item.shipmentNumber}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Form>
            {searchSno && (
              <Tooltip title={'Search'}>
                <CheckOutlined className="rightIcon" onClick={onSearchEquipmentSno} />
              </Tooltip>
            )}
            <Tooltip title={'Clear Search'}>
              <CloseOutlined
                className="closeIcon"
                onClick={() => {
                  setSearchOpen(false);
                  equipSnoSearchForm.resetFields();
                  setEquipSnoList(equipSnoDrop);
                }}
              />
            </Tooltip>
          </div>
        )}
      </div>
      <Divider
        orientation="left"
        className="divider"
        style={{ marginTop: searchOpen ? '4px' : '16px' }}
      >
        <Tooltip placement="top" title="Add New Shipment Number">
          <PlusOutlined className="addNewSerialNo" onClick={handleAddButtonClick} />
        </Tooltip>
        <Text>Shipments</Text>
      </Divider>

      <Skeleton loading={fetchLoading} active>
        <List
          className="checksList-equip"
          size="large"
          bordered
          dataSource={equipSnoList}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Tooltip
                  key={'view'}
                  title={`View ${item.shipmentNumber} History`}
                  placement="right"
                  onClick={() => handleEyeClick(item)}
                >
                  <EyeOutlined className="sno_icon" key={'view'} />
                </Tooltip>,
                <DeleteOutlined
                  className="sno_icon"
                  key={'delete'}
                  onClick={() => {
                    showDeleteModal(item);
                  }}
                />,
              ]}
            >
              <Text>{item.shipmentNumber}</Text>
            </List.Item>
          )}
          loading={fetchLoading}
        />
      </Skeleton>

      {isTableModelVisible && (
        <ShipmentLogs
          showFormModel={isTableModelVisible}
          setShowFormModel={setIsTableModelVisible}
          selectedData={selectedDataSno}
        />
      )}

      <Modal
        width="45vw"
        className="pipeline-modal"
        title="Confirm New Shipment Number"
        centered
        visible={isConfirmModalVisible}
        footer={[
          <Button key="cancel" onClick={handleCancelAdd}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmAdd} loading={addLoading}>
            Confirm
          </Button>,
        ]}
      >
        <div>
          <Text style={{ fontSize: '16px' }}>
            Do you want to add the following shipment number?
          </Text>
          <p
            style={{
              textAlign: 'center',
              marginTop: '20px',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#EF314C',
            }}
          >
            {previewShipmentNumber}
          </p>
        </div>
      </Modal>

      <Modal
        width="45vw"
        className="pipeline-modal"
        title="Delete Shipment Number"
        centered
        visible={isDeleteModalVisible}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="apply" type="primary" onClick={handleOk} loading={deleteLoading}>
            Delete
          </Button>,
        ]}
      >
        <div>
          <Text style={{ fontSize: '16px' }}>
            <p>
              Deleting shipment number will delete all the corresponding Stations and related
              information.
            </p>
            Are you sure you want to delete{' '}
            <b style={{ color: '#EF314C' }}>{selectedDataSno && selectedDataSno.shipmentNumber}</b>{' '}
            shipment number?
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default ShipmentNo;
